import React from 'react';
import { Box, Paper, SxProps, Theme } from '@mui/material';

interface ListSurfaceProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

interface ListSurfaceSectionProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

interface ListSurfaceComponent extends React.FC<ListSurfaceProps> {
  Toolbar: React.FC<ListSurfaceSectionProps>;
  Content: React.FC<ListSurfaceSectionProps>;
  Footer: React.FC<ListSurfaceSectionProps>;
}

const mergeSx = (base: SxProps<Theme>, override?: SxProps<Theme>): SxProps<Theme> => {
  if (!override) return base;
  return Array.isArray(override) ? [base, ...override] : [base, override];
};

/** Shared surface for searchable, filterable lists with responsive rows and pagination. */
const ListSurface = Object.assign(
  ({ children, sx }: ListSurfaceProps) => (
    <Paper
      sx={mergeSx({
        minWidth: 0,
        border: 0,
        borderRadius: { xs: 0, md: 2 },
        overflow: 'hidden',
        boxShadow: { xs: 'none', md: '0 8px 24px rgba(32, 55, 44, .08)' },
      }, sx)}
    >
      {children}
    </Paper>
  ),
  {
    Toolbar: ({ children, sx }: ListSurfaceSectionProps) => (
      <Box sx={mergeSx({ px: { xs: 2, md: 2.5 }, pt: { xs: 1.5, md: 2.5 }, pb: { xs: 1.5, md: 2.5 } }, sx)}>
        {children}
      </Box>
    ),
    Content: ({ children, sx }: ListSurfaceSectionProps) => (
      <Box sx={mergeSx({ minWidth: 0 }, sx)}>{children}</Box>
    ),
    Footer: ({ children, sx }: ListSurfaceSectionProps) => (
      <Box component="footer" sx={mergeSx({ minWidth: 0 }, sx)}>{children}</Box>
    ),
  },
) as ListSurfaceComponent;

export default ListSurface;
